import { DateTime } from 'luxon';

export const DATE_DISPLAY_FORMAT = 'MM/dd/yyyy';
export const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

export const TIME_DISPLAY_FORMAT = 'hh:mm a';
export const TIME_INPUT_FORMAT = 'HH:mm';

export const DATE_TIME_DISPLAY_FORMAT = `${DATE_DISPLAY_FORMAT} ${TIME_DISPLAY_FORMAT}`;
export const DATE_TIME_INPUT_FORMAT = `${DATE_INPUT_FORMAT} ${TIME_INPUT_FORMAT}`;

export const isValidDateValue = (value: string | null): boolean => {
    if (!value) return false;

    return DateTime.fromFormat(value, DATE_DISPLAY_FORMAT).isValid;
};

export const isValidTimeValue = (value: string | null): boolean => {
    if (!value) return false;

    return DateTime.fromFormat(value, TIME_DISPLAY_FORMAT).isValid;
};

export const isValidDateTimeValue = (value: string | null): boolean => {
    if (!value) return false;

    return DateTime.fromFormat(value, DATE_TIME_DISPLAY_FORMAT).isValid;
};

export const CALENDAR_DATE_FORMAT = 'yyyy-MM-dd';

export type TimeOption = { label: string; value: string };

const TIME_OPTION_INTERVAL_MINUTES = 15;

/** Formats an HH:mm value as a display label via a fixed UTC reference — DST-day safe. */
export const timeValueToLabel = (time: string): string => {
    const [hour, minute] = time.split(':').map(Number);
    return DateTime.utc(2024, 1, 1, hour, minute).toFormat(TIME_DISPLAY_FORMAT);
};

export const getTimeOptions = (): TimeOption[] =>
    Array.from({ length: (24 * 60) / TIME_OPTION_INTERVAL_MINUTES }, (_, index) => {
        const totalMinutes = index * TIME_OPTION_INTERVAL_MINUTES;
        const value = `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(
            2,
            '0',
        )}`;
        return { label: timeValueToLabel(value), value };
    });

/** Ensures a hydrated off-grid time (e.g. legacy 10:07) still appears as a selectable option. */
export const withInjectedTimeOptions = (options: TimeOption[], times: (string | null)[]): TimeOption[] => {
    const missing = times.filter((time): time is string => !!time && !options.some((option) => option.value === time));
    if (!missing.length) return options;
    return [...options, ...missing.map((time) => ({ label: timeValueToLabel(time), value: time }))].sort((a, b) =>
        a.value.localeCompare(b.value),
    );
};
